import React, { useEffect, useRef, useState } from 'react';
import { Alert, Grid, MenuItem, Paper, TextField } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Btn, InputController, ResourcesList, setProgress } from 'Components';
import Yup from 'Utils/Yup';
import { mapValidationErrors } from 'Utils/Http/MapValidationErrors';
import { CERTIFICATE_CATEGORIES, NO_CERTIFICATE_CATEGORY } from 'Constants/CertificateNames';
import { printCertificateForName } from 'Services/CertificateNamesSvc';

const PRINT_CERTIFICATE_BTN = 'PRINT_CERTIFICATE_BTN';
// Btn wires itself to the progress service by name, so the two buttons beside
// the preview need their own even though nothing ever puts them in progress.
const OPEN_IN_TAB_BTN = 'OPEN_CERTIFICATE_IN_TAB_BTN';
const DOWNLOAD_BTN = 'DOWNLOAD_CERTIFICATE_BTN';

const schema = Yup.object({
    name: Yup.string().trim().required(),
});

/**
 * Print a certificate for someone who is not on the approved list.
 *
 * The counterpart to the Verify Name tool: that one answers "is this name
 * approved?", this one skips the question entirely and prints whatever is
 * typed. It is for the case the approved list cannot cover — a member added
 * after the list was fixed, or one whose name is spelled differently on it.
 *
 * Nothing here is recorded in the verification or print history, because no
 * verification happened. The name is not added to the approved list either; use
 * the Certificate Names page for that.
 *
 * The PDF is shown inline rather than opened in a new tab: an async response
 * arrives long after the click, which is exactly what pop-up blockers stop. The
 * browser's own PDF viewer supplies the print button inside the frame, and the
 * two buttons beside it cover printing from a full tab or keeping the file.
 */
const PrintCertificatePage = () => {
    const { t } = useTranslation();
    const spacing = 4;

    // What is currently rendered: the object URL of the PDF and the name it was
    // printed for, so the heading above the preview cannot drift from the file.
    const [preview, setPreview] = useState(null);

    // Object URLs hold the blob in memory until they are revoked, and this page
    // is meant to be used name after name. The ref is what lets the revoke
    // happen on unmount as well as on replacement.
    const previewUrl = useRef(null);

    const [category, setCategory] = useState(NO_CERTIFICATE_CATEGORY);

    const form = useForm({
        resolver: yupResolver(schema),
        defaultValues: { name: '' },
    });
    const { control, handleSubmit, setError } = form;

    const showPreview = (url, name) => {
        if (previewUrl.current) {
            URL.revokeObjectURL(previewUrl.current);
        }

        previewUrl.current = url;
        setPreview(url ? { url, name } : null);
    };

    useEffect(
        () => () => {
            if (previewUrl.current) {
                URL.revokeObjectURL(previewUrl.current);
            }
        },
        [],
    );

    const handleFormSubmit = async (values) => {
        setProgress(PRINT_CERTIFICATE_BTN, true);
        try {
            const pdf = await printCertificateForName({ name: values.name, category });

            showPreview(URL.createObjectURL(pdf), values.name.trim());
        } catch (error) {
            // A certificate left on screen next to a failed request reads as if
            // it were the one that had just been asked for.
            showPreview(null, null);

            const fieldErrors = error?.response?.data?.errors;

            if (error?.response?.status === 422 && fieldErrors) {
                mapValidationErrors(fieldErrors).forEach(({ name, message, type }) => {
                    setError(name, { type, message });
                });
            } else if (error?.response) {
                toast.error(
                    error?.response?.data?.message ??
                        t('admin.certificateNames.print.failed'),
                );
            }

            // No `response` at all means the certificate rendered and only its
            // transfer was lost — the dev server drops one large body in six.
            // The service has already retried three times by this point, and
            // being told something failed when the certificate itself is fine
            // was more alarming than useful, so this case says nothing. The
            // operator presses the button again.
        }
        setProgress(PRINT_CERTIFICATE_BTN, false);
    };

    const handleOpenInTab = () => {
        if (preview) {
            window.open(preview.url, '_blank', 'noopener,noreferrer');
        }
    };

    const handleDownload = () => {
        if (!preview) return;

        const link = document.createElement('a');
        link.href = preview.url;
        link.download = `certificate-${preview.name}.pdf`;
        link.click();
    };

    return (
        <ResourcesList
            headerProps={{ title: t('admin.certificateNames.print.title') }}
            Section={
                <div className="mt-8">
                    <p className="text-xs text-gray-500 pb-4">
                        {t('admin.certificateNames.print.intro')}
                    </p>

                    <form onSubmit={handleSubmit(handleFormSubmit)}>
                        <Grid container spacing={spacing} alignItems="flex-end">
                            <Grid item xs={12} md={6}>
                                <InputController
                                    control={control}
                                    name="name"
                                    label={t('admin.certificateNames.print.nameLabel')}
                                    placeholder={t('admin.certificateNames.print.namePlaceholder')}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <h6 className="labelForm pb-2">
                                    {t('admin.certificateNames.print.categoryLabel')}
                                </h6>
                                <TextField
                                    select
                                    fullWidth
                                    id="print-certificate-category"
                                    value={category}
                                    onChange={(event) => setCategory(event.target.value)}
                                    // MUI renders a blank box for an empty value
                                    // unless told to show the option.
                                    SelectProps={{ displayEmpty: true }}
                                >
                                    <MenuItem value={NO_CERTIFICATE_CATEGORY}>
                                        {t('admin.certificateNames.noCategory')}
                                    </MenuItem>
                                    {CERTIFICATE_CATEGORIES.map((value) => (
                                        <MenuItem key={value} value={value}>
                                            {t(`admin.certificateNames.categories.${value}`)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Btn fullWidth={false} type="submit" name={PRINT_CERTIFICATE_BTN}>
                                    {t('admin.certificateNames.print.submit')}
                                </Btn>
                            </Grid>
                        </Grid>
                    </form>

                    <Alert severity="info" className="mt-6">
                        {t('admin.certificateNames.print.categoryHint')}
                    </Alert>

                    {preview ? (
                        <Paper variant="outlined" className="mt-8 p-4">
                            <div className="flex flex-wrap items-center justify-between gap-3 pb-3">
                                <div>
                                    <h6 className="labelForm text-gray-500">
                                        {t('admin.certificateNames.print.previewFor')}
                                    </h6>
                                    <div className="text-base font-medium break-words">
                                        {preview.name}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Btn
                                        fullWidth={false}
                                        name={OPEN_IN_TAB_BTN}
                                        variant="outlined"
                                        onClick={handleOpenInTab}
                                    >
                                        {t('admin.certificateNames.print.openInTab')}
                                    </Btn>
                                    <Btn
                                        fullWidth={false}
                                        name={DOWNLOAD_BTN}
                                        variant="outlined"
                                        onClick={handleDownload}
                                    >
                                        {t('admin.certificateNames.print.download')}
                                    </Btn>
                                </div>
                            </div>

                            {/* A4 landscape, so the frame is given the same ratio
                                as the page it is showing. */}
                            <iframe
                                key={preview.url}
                                src={preview.url}
                                title={t('admin.certificateNames.print.previewTitle')}
                                className="w-full border border-gray-200 rounded"
                                style={{ aspectRatio: '842 / 595', minHeight: 420 }}
                            />

                            <p className="text-xs text-gray-500 pt-3">
                                {t('admin.certificateNames.print.previewHint')}
                            </p>
                        </Paper>
                    ) : (
                        <p className="text-sm text-gray-500 mt-8">
                            {t('admin.certificateNames.print.noResultYet')}
                        </p>
                    )}
                </div>
            }
        />
    );
};

export default PrintCertificatePage;
